import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import ProjectsAutoComplete from 'src/components/ProjectsAutoComplete';
import { AssignedProjectsAutoComplete } from 'src/components/AssignedProjectsAutoComplete';
import { IActivity } from 'src/models/Activity';
import { IProjectDetails, IProjectResponse } from 'src/models/Project';

import {
  assignActivityToProject,
  fetchProjectsById,
  getProjectById,
} from 'src/redux/features/projects/projectsSlice';
import { getUserRoles } from 'src/redux/features/users/usersSlice';
import { isAuthorised } from 'src/utils/helpers';
import { Checkbox, Flex, Input, Modal, Spin, Typography } from 'antd';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';

interface CopyModalProps {
  showCopyModal: boolean;
  handleClose: () => void;
  isModalOpen: boolean;
  selectedProject: string;
  selectedActivity: IActivity[];
}

export const CopyActivityModal: React.FC<CopyModalProps> = ({
  showCopyModal,
  handleClose,
  isModalOpen,
  selectedProject,
  selectedActivity,
}) => {
  const { Title, Text } = Typography;
  const [selectedProjectData, setSelectedProjectData] =
    useState<IProjectResponse | null>(null);
  const [activityData, setActivityData] = useState<IActivity[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState<IActivity[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const {
    data: activityDetails,
    loading: activitiesLoading,
  }: { data: IProjectDetails; loading: boolean } = useSelector(getProjectById);
  const userRoles = useSelector(getUserRoles);
  const onSelectedProjectChange = (selectedItem: IProjectResponse) => {
    if (selectedItem) {
      setSelectedProjectData(selectedItem);
      getActivitiesByProjectId(selectedItem.id);
    } else {
      setSelectedProjectData(null);
    }
  };

  const getActivitiesByProjectId = async (id: number) => {
    setIsLoading(true);
    try {
      await dispatch(fetchProjectsById({ id }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (activity: IActivity) => {
    setSelectedActivities((prevState) =>
      prevState.some((a) => a.id === activity.id)
        ? prevState.filter((a) => a.id !== activity.id)
        : [...prevState, activity],
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities(selectedActivity.filter((item) => !item.isDefault));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    const filteredData = selectedActivity.filter((item) => !item.isDefault);

    if (selectedActivities.length === filteredData.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedActivities, selectedActivity]);

  const handleCopySave = async () => {
    if (!selectedProjectData) {
      dispatch(setError(en.Activities.Error.selectTargetProject));

      return;
    }

    const targetActivityNames = activityData.map((activity) => activity.name);
    const hasDuplicates = selectedActivities.some((activity) =>
      targetActivityNames.includes(activity.name),
    );

    if (hasDuplicates) {
      dispatch(setError(en.Activities.Error.activitiesAlreadyExist));

      return;
    }

    setIsSaving(true);

    const response = await dispatch(
      assignActivityToProject({
        id: selectedProjectData?.id,
        payload: {
          activities: selectedActivities.map((activity) => ({
            ...activity,
            id: 0,
          })),
        },
      }),
    );

    setIsSaving(false);
    if (response.error) {
      return;
    }
    toast.success(en.Activities.Success.activitiesCopied);
    handleClose();
  };

  useEffect(() => {
    if (selectedProjectData?.id) {
      setActivityData(
        activityDetails.activityTypes.filter(
          (activity) => !activity.isDefault,
        ) as IActivity[],
      );
    }
  }, [selectedProjectData?.id, activityDetails]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedProjectData(null);
      setActivityData([]);
      setSelectedActivities([]);
      setSelectAll(false);
    }
  }, [isModalOpen]);

  // Inside the CopyActivityModal component
  return (
    <Modal
      open={showCopyModal}
      onCancel={handleClose}
      title={
        <Title level={4} style={{ padding: '1rem 1rem 0', margin: 0 }}>
          {en.Activities.Title.copyActivity}
        </Title>
      }
      maskClosable={false}
      footer={
        <Flex
          justify="flex-end"
          gap={'middle'}
          style={{ padding: '0 1rem 1rem' }}
        >
          <AntDButton
            type="primary"
            onClick={handleCopySave}
            disabled={!selectedActivities.length || isSaving}
            htmlType="button"
            loading={isSaving}
            status="Saving..."
            statusOnComplete="Save"
          />
        </Flex>
      }
      width={800}
      centered
    >
      <div style={{ padding: '1rem' }}>
        <Flex gap={'middle'}>
          <Flex
            vertical
            style={{
              width: '50%',
              border: '0.063rem solid #d9d9d9',
              padding: '0.75rem',
              borderRadius: '0.5rem',
            }}
          >
            <Title level={5}>{en.Activities.Title.selectSourceProject}</Title>

            <Input
              value={selectedProject}
              disabled
              style={{
                width: '100%',
                marginBottom: '0.75rem',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            />

            <div
              style={{
                maxHeight: '25rem',
                overflowY: 'auto',
              }}
            >
              <div
                style={{
                  marginBottom: '0.75rem',
                  backgroundColor: '#F5F5F5',
                  padding: '0.25rem 0',
                }}
              >
                <Checkbox checked={selectAll} onChange={handleSelectAllChange}>
                  <Text strong>Select All Activities</Text>
                </Checkbox>
              </div>

              {selectedProject && selectedActivity.length ? (
                selectedActivity
                  .filter((val) => !val.isNew)
                  .map((item) => (
                    <Flex
                      key={item.id}
                      align="center"
                      style={{ marginBottom: '0.75rem' }}
                    >
                      {!item.isDefault ? (
                        <Checkbox
                          checked={selectedActivities.some(
                            (a) => a.id === item.id,
                          )}
                          onChange={() => handleCheckboxChange(item)}
                          style={{ marginRight: '0.75rem' }}
                        />
                      ) : (
                        <div
                          style={{ width: '1.25rem', marginRight: '0.75rem' }}
                        />
                      )}
                      <span>{item.name}</span>
                    </Flex>
                  ))
              ) : (
                <Text type="secondary">No Data available</Text>
              )}
            </div>
          </Flex>

          <Flex
            vertical
            style={{
              width: '50%',
              border: '0.063rem solid #d9d9d9',
              padding: '0.75rem',
              borderRadius: '0.5rem',
            }}
          >
            <Title level={5}>{en.Activities.Title.selectTargetProject}</Title>

            <div
              style={{
                marginBottom: '0.75rem',
                textAlign: 'center',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isAuthorised(userRoles.data, ['CapexAdmin']) ? (
                <ProjectsAutoComplete
                  onSelectedProjectChange={onSelectedProjectChange}
                  style={{
                    width: '100%',
                  }}
                  selectedProject={selectedProject}
                  selectedTargetProjectId={selectedProjectData?.id}
                />
              ) : (
                <AssignedProjectsAutoComplete
                  onSelectedProjectChange={onSelectedProjectChange}
                  style={{ width: '100%', textAlign: 'center' }}
                  selectedProject={selectedProject}
                  selectedTargetProjectId={selectedProjectData?.id}
                />
              )}
            </div>

            <div
              style={{
                height: '25rem',
                overflowY: 'auto',
              }}
            >
              {isLoading ? (
                <Spin
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : selectedProjectData?.id && activityData.length ? (
                activityData.map((item) => (
                  <Flex
                    key={item.id}
                    align="center"
                    style={{ marginBottom: '0.75rem' }}
                  >
                    <span>{item.name}</span>
                  </Flex>
                ))
              ) : (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="secondary">No Data available</Text>
                </div>
              )}
            </div>
          </Flex>
        </Flex>
      </div>
    </Modal>
  );
};
